// Order address picker/editor modal (CEO 2026-07-21). Opened from the
// billing/shipping blocks on the estimate/SO document (OrderDetailPage).
// Mirrors the house modal idiom (CloneTargetModal): a radio-list of the
// client's SAVED addresses (label + full formatted address) plus an
// "Add new address" section using the SAME fields as the client Addresses
// tab. Saving a new address POSTs to the existing client-addresses endpoint
// (autosave to the client's saved list — CEO ask #1) and then applies it to
// the order. Applying = the parent PATCHes the order (billing/shipping id),
// which re-renders the snapshot server-side.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { COUNTRY_OPTIONS, REGION_OPTIONS, normalizeCountryCode, normalizeStateCode } from "@merchd/shared";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

interface AddressRow {
  id: string;
  label: string;
  companyName: string | null;
  attention: string | null;
  street1: string;
  street2: string | null;
  city: string;
  stateCode: string;
  postalCode: string;
  countryCode: string | null;
}

/** Render a saved address as multi-line text for the radio list. */
function formatAddressLines(a: AddressRow): string[] {
  const lines: string[] = [];
  const who = [a.companyName, a.attention].filter(Boolean).join(" · ");
  if (who) lines.push(who);
  if (a.street1) lines.push(a.street1);
  if (a.street2) lines.push(a.street2);
  const cityLine = [a.city, a.stateCode, a.postalCode].filter(Boolean).join(", ");
  if (cityLine) lines.push(cityLine);
  if (a.countryCode && a.countryCode !== "US") lines.push(a.countryCode);
  return lines;
}

const SELECT =
  "h-9 w-full rounded-md border border-brown-100 bg-white px-3 text-sm text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200";

/** What to tell the rep for each field, in plain words. */
const FIX_IT: Record<string, string> = {
  label: "Add Address Type",
  street_1: "Add Street",
  city: "Add City",
  state_code: "Choose State",
  postal_code: "Add Postal Code",
  country_code: "Choose Country",
};

export function OrderAddressModal(props: {
  /** "Billing" | "Shipping" — for the title + apply routing. */
  kind: "billing" | "shipping";
  clientId: string;
  /** Currently-applied address id, preselected in the radio list. */
  currentAddressId: string | null;
  /** True while the parent's PATCH is in flight. */
  applying: boolean;
  onCancel: () => void;
  /** Apply a chosen (or freshly-saved) address to the order. */
  onApply: (addressId: string) => void;
}) {
  const { kind, clientId, currentAddressId, applying, onCancel, onApply } =
    props;
  const queryClient = useQueryClient();
  const title = kind === "billing" ? "Billing address" : "Shipping address";

  const { data, isLoading } = useQuery({
    queryKey: ["clients", clientId, "addresses"],
    queryFn: () =>
      api<{ addresses: AddressRow[] }>(`/clients/${clientId}/addresses`),
    enabled: clientId.length > 0,
  });
  const addresses = useMemo(() => data?.addresses ?? [], [data]);

  const [selectedId, setSelectedId] = useState<string | null>(currentAddressId);
  useEffect(() => {
    setSelectedId((prev) => prev ?? currentAddressId);
  }, [currentAddressId]);

  const [isCreating, setIsCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [company, setCompany] = useState("");
  const [street1, setStreet1] = useState("");
  const [street2, setStreet2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postal, setPostal] = useState("");
  const [country, setCountry] = useState("US");
  const [error, setError] = useState<string | null>(null);

  // Close on ESC (fixed overlay).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !applying) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [applying, onCancel]);

  // Autosave a new address to the client's saved list, then apply it to the
  // order. Field names are snake_case with the underscore (street_1) — the
  // route schema rejects street1 (regression from PR #99, see
  // ClientAddressesTab).
  const createMutation = useMutation({
    mutationFn: (body: {
      label: string;
      company_name?: string;
      attention?: string;
      street_1: string;
      street_2?: string;
      city: string;
      state_code: string;
      postal_code: string;
      country_code?: string;
      is_default_ship_to?: boolean;
      is_default_bill_to?: boolean;
    }) =>
      api<{ address: { id: string } }>(`/clients/${clientId}/addresses`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "addresses"],
      });
      // Apply the freshly-saved address to the order immediately.
      onApply(res.address.id);
    },
    onError: (err) => {
      // A plain sentence saying what to fix, never a bare status code
      // (Stephanie 2026-10-13: "(400)" and "check State" were both unhelpful).
      const details = err instanceof ApiError ? (err.body as { details?: Record<string, unknown> } | null)?.details : null;
      const field = details ? Object.keys(details).find((k) => k !== "_errors") : undefined;
      setError(
        (field && FIX_IT[field]) ??
          "Flow couldn't save this address, and nothing was saved. Try again — if it keeps happening, tell Jake.",
      );
    },
  });

  const submitNew = (): void => {
    // Say exactly what's missing, one thing at a time, before trying to save.
    const missing =
      label.trim().length === 0 ? "label"
      : street1.trim().length === 0 ? "street_1"
      : city.trim().length === 0 ? "city"
      : state.trim().length < 2 ? "state_code"
      : postal.trim().length < 3 ? "postal_code"
      : null;
    if (missing) {
      setError(FIX_IT[missing]!);
      return;
    }
    setError(null);
    createMutation.mutate({
      label: label.trim(),
      company_name: company.trim().length > 0 ? company.trim() : undefined,
      street_1: street1.trim(),
      street_2: street2.trim().length > 0 ? street2.trim() : undefined,
      city: city.trim(),
      state_code: normalizeStateCode(state),
      postal_code: postal.trim(),
      country_code: country.trim() ? normalizeCountryCode(country) : "US",
      // Set the new address as the client's default for this role too, so it
      // becomes the sensible default on future orders.
      is_default_ship_to: kind === "shipping",
      is_default_bill_to: kind === "billing",
    });
  };

  const busy = applying || createMutation.isPending;
  const regionOptions = country === "US" || country === "CA" ? REGION_OPTIONS[country] : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-address-modal-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-brown-100 bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-brown-50 px-6 py-4">
          <h2
            id="order-address-modal-title"
            className="font-display text-xl font-semibold text-brown-500"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-brown-300 hover:text-brown-500 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 py-5">
          {!isCreating && (
            <div>
              <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
                Saved addresses
              </p>
              {isLoading ? (
                <p className="text-sm text-brown-300">Loading addresses…</p>
              ) : addresses.length === 0 ? (
                <p className="rounded-md border border-dashed border-brown-100 bg-cream-50/40 px-3 py-4 text-center text-sm text-brown-300">
                  No saved addresses yet — add one below.
                </p>
              ) : (
                <ul className="space-y-2">
                  {addresses.map((a) => {
                    const checked = selectedId === a.id;
                    return (
                      <li key={a.id}>
                        <label
                          className={
                            "flex cursor-pointer gap-3 rounded-md border px-3 py-2.5 text-sm " +
                            (checked
                              ? "border-ink bg-canvas"
                              : "border-brown-100 bg-white hover:border-peach-300")
                          }
                        >
                          <input
                            type="radio"
                            name="order-address"
                            checked={checked}
                            onChange={() => setSelectedId(a.id)}
                            className="mt-1 h-4 w-4 text-ink focus:ring-ink/30"
                            disabled={busy}
                          />
                          <span className="min-w-0">
                            <span className="block font-semibold text-brown-500">
                              {a.label}
                            </span>
                            <span className="block text-brown-400">
                              {formatAddressLines(a).map((line, i) => (
                                <span key={i} className="block">
                                  {line}
                                </span>
                              ))}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {isCreating ? (
            <div className="rounded-lg border border-peach-300 bg-peach-50 p-4">
              <div className="mb-3 font-semibold text-brown-500">
                New address
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                    Address Type
                  </span>
                  <Input
                    autoFocus
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    maxLength={120}
                    placeholder="HQ / Warehouse"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                    Company Name{" "}
                    <span className="font-normal text-brown-300">
                      (optional)
                    </span>
                  </span>
                  <Input
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    maxLength={200}
                    placeholder="Acme Holdings"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                    Street
                  </span>
                  <Input
                    value={street1}
                    onChange={(e) => setStreet1(e.target.value)}
                    maxLength={200}
                    placeholder="123 Main St"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                    Street 2{" "}
                    <span className="font-normal text-brown-300">
                      (optional)
                    </span>
                  </span>
                  <Input
                    value={street2}
                    onChange={(e) => setStreet2(e.target.value)}
                    maxLength={200}
                    placeholder="Suite 200"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                    City
                  </span>
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    maxLength={120}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                    State
                  </span>
                  {regionOptions ? (
                    <select
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      autoComplete="address-level1"
                      className={SELECT}
                    >
                      <option value="">Choose…</option>
                      {regionOptions.map((r) => (
                        <option key={r.code} value={r.code}>{r.name}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      autoComplete="address-level1"
                      maxLength={3}
                    />
                  )}
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                    Postal code
                  </span>
                  <Input
                    value={postal}
                    onChange={(e) => setPostal(e.target.value)}
                    autoComplete="postal-code"
                    maxLength={20}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                    Country
                  </span>
                  <select
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      setState("");
                    }}
                    autoComplete="country"
                    className={SELECT}
                  >
                    {COUNTRY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setIsCreating(true);
                setError(null);
              }}
              disabled={busy}
              className="text-sm font-semibold text-ink underline underline-offset-4 hover:text-muted disabled:opacity-50"
            >
              + Add new address
            </button>
          )}

          {error && <p className="text-sm text-rust-600">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-brown-50 px-6 py-3">
          {isCreating ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setIsCreating(false);
                  setError(null);
                }}
                disabled={busy}
                className="px-3 py-1.5 text-sm font-semibold text-ink underline underline-offset-4 hover:text-muted disabled:opacity-50"
              >
                Back
              </button>
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={submitNew}
              >
                <Check className="h-3.5 w-3.5" />{" "}
                {busy ? "Saving…" : "Save & apply"}
              </Button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="px-3 py-1.5 text-sm font-semibold text-ink underline underline-offset-4 hover:text-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <Button
                type="button"
                size="sm"
                disabled={busy || selectedId === null}
                onClick={() => selectedId && onApply(selectedId)}
              >
                <Check className="h-3.5 w-3.5" />{" "}
                {applying ? "Applying…" : "Apply"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
